import React from 'react';
import {
  Box,
  Container,
  Typography,
  Avatar,
  Chip,
  Grid,
  Paper,
  Divider,
  Button,
  useTheme,
  List,
  ListItem,
  ListItemText,
  IconButton
} from '@material-ui/core';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import PhoneIcon from '@material-ui/icons/Phone';
import EmailIcon from '@material-ui/icons/Email';
import LocationOnIcon from '@material-ui/icons/LocationOn';
import { useParams, Link } from 'react-router-dom';
import { makeStyles } from '@material-ui/core/styles';
import { members } from '../../data/members';

// Custom styles using makeStyles
const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(6, 0),
    marginTop: '100px',
    background: theme.palette.background.default
  },
  paper: {
    padding: theme.spacing(4),
    borderRadius: theme.spacing(2),
    boxShadow: theme.shadows[5],
    background: theme.palette.background.paper,
    transition: 'transform 0.3s ease, box-shadow 0.3s ease',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: theme.shadows[10]
    }
  },
  avatar: {
    width: theme.spacing(20),
    height: theme.spacing(20),
    margin: 'auto',
    border: `4px solid ${theme.palette.primary.main}`,
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.05)'
    }
  },
  backButton: {
    marginBottom: theme.spacing(3),
    textTransform: 'none',
    fontWeight: 600,
    color: theme.palette.primary.main,
    borderColor: theme.palette.primary.main,
    '&:hover': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.primary.contrastText
    }
  },
  sectionTitle: {
    fontWeight: 700,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(2)
  },
  chip: {
    margin: theme.spacing(0.5),
    backgroundColor: theme.palette.common.black,
    color: theme.palette.secondary.contrastText,
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.secondary.main
    }
  },
  divider: {
    margin: theme.spacing(3, 0),
    backgroundColor: theme.palette.divider
  },
  educationBox: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    marginBottom: theme.spacing(1),
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[200]
    }
  },
  experienceBox: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[50],
    marginBottom: theme.spacing(2),
    transition: 'background-color 0.3s ease',
    '&:hover': {
      backgroundColor: theme.palette.grey[100]
    }
  },
  contactBox: {
    padding: theme.spacing(2),
    borderRadius: theme.spacing(1),
    backgroundColor: theme.palette.grey[100],
    marginBottom: theme.spacing(2)
  },
  link: {
    textDecoration: 'none',
    color: theme.palette.primary.main,
    '&:hover': {
      textDecoration: 'underline'
    }
  },
  icon: {
    marginRight: theme.spacing(1),
    color: theme.palette.primary.main
  }
}));

const MemberDetail = () => {
  const { name } = useParams();
  const member = members.find((p) => p.name === decodeURIComponent(name));
  const classes = useStyles();
  const theme = useTheme();

  if (!member) {
    return (
      <Container className={classes.root}>
        <Typography variant="h6" color="textSecondary">
          Member not found.
        </Typography>
        <Link to="/" className={classes.link}>
          Back to list
        </Link>
      </Container>
    );
  }

  return (
    <Container className={classes.root}>
      
      <Button
        startIcon={<ArrowBackIcon />}
        component={Link}
        to="/members"
        style={{ marginBottom: '30px' }}
      >
        Back to Members
      </Button>

      <Paper className={classes.paper}>
        <Grid container spacing={4}>
          <Grid item xs={12} md={3} style={{ textAlign: 'center' }}>
            <Avatar
              src={member.avatar}
              alt={member.name}
              className={classes.avatar}
            />
            <Typography variant="h5" className={classes.sectionTitle} mt={2}>
              {member.name}
            </Typography>
            <Typography variant="subtitle1" color="textSecondary">
              {member.title}
            </Typography>
            {member.contact && (
              <Box className={classes.contactBox} mt={2}>
                <Typography variant="h6" className={classes.sectionTitle}>
                  Contact
                </Typography>
                {member.contact.phone && (
                  <ListItem disableGutters>
                    <IconButton className={classes.icon}>
                      <PhoneIcon />
                    </IconButton>
                    <ListItemText primary={member.contact.phone} />
                  </ListItem>
                )}
                {member.contact.email && (
                  <ListItem disableGutters>
                    <IconButton className={classes.icon}>
                      <EmailIcon />
                    </IconButton>
                    <ListItemText primary={member.contact.email} />
                  </ListItem>
                )}
                {member.contact.address && (
                  <ListItem disableGutters>
                    <IconButton className={classes.icon}>
                      <LocationOnIcon />
                    </IconButton>
                    <ListItemText primary={member.contact.address} />
                  </ListItem>
                )}
              </Box>
            )}
          </Grid>

          <Grid item xs={12} md={9}>
            <Typography variant="h5" className={classes.sectionTitle}>
              Summary
            </Typography>
            <Typography variant="body1" paragraph>
              {member.summary}
            </Typography>

            <Divider className={classes.divider} />

            <Typography variant="h5" className={classes.sectionTitle}>
              Education
            </Typography>
            {Array.isArray(member.education) ? (
              member.education.map((edu, i) => (
                <Box key={i} className={classes.educationBox}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {edu.school}
                  </Typography>
                  <Typography>{edu.degree}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {/* {edu.years} - */ } {edu.location}
                  </Typography>
                  {edu.gpa && (
                    <Typography variant="body2" color="textSecondary">
                      GPA: {edu.gpa}
                    </Typography>
                  )}
                </Box>
              ))
            ) : (
              <Box className={classes.educationBox}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {member.education.school}
                </Typography>
                <Typography>{member.education.degree}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {/*{member.education.years} -*/} {member.education.location}
                </Typography>
                {member.education.gpa && (
                  <Typography variant="body2" color="textSecondary">
                    GPA: {member.education.gpa}
                  </Typography>
                )}
              </Box>
            )}

            <Divider className={classes.divider} />

            <Typography variant="h5" className={classes.sectionTitle}>
              Skills
            </Typography>
            {Object.entries(member.skills).map(([key, value], i) => (
              <Box key={i} mb={2}>
                <Typography
                  variant="subtitle2"
                  fontWeight="bold"
                  style={{ textTransform: 'capitalize' }}
                >
                  {key.replace('_', ' ')}
                </Typography>
                <Box display="flex" flexWrap="wrap">
                  {value.map((skill, j) => (
                    <Chip key={j} label={skill} className={classes.chip} />
                  ))}
                </Box>
              </Box>
            ))}

            <Divider className={classes.divider} />

            <Typography variant="h5" className={classes.sectionTitle}>
              Languages
            </Typography>
            <Box className={classes.educationBox}>
              {member.languages.map((lang, i) => (
                <Typography key={i} variant="body2">
                  <strong>{lang.language}:</strong> {lang.level}
                </Typography>
              ))}
            </Box>

            <Divider className={classes.divider} />

            <Typography variant="h5" className={classes.sectionTitle}>
              Experience
            </Typography>
            {(member.experience || member.projects || []).map((exp, i) => (
              <Box key={i} className={classes.experienceBox}>
                <Typography variant="subtitle1" fontWeight="bold">
                  {exp.project || exp.company}
                </Typography>
                {exp.role && (
                  <Typography variant="body2" color="textSecondary">
                    {exp.role}
                  </Typography>
                )}
                <Typography variant="body2" color="textSecondary">
                  {exp.period}
                </Typography>
                <Typography variant="body2" mt={1}>
                  {exp.description}
                </Typography>
                <List style={{ marginTop: theme.spacing(1), paddingLeft: theme.spacing(3) }}>
                  {exp.responsibilities?.map((r, idx) => (
                    <ListItem key={idx} disableGutters>
                      <ListItemText primary={r} />
                    </ListItem>
                  ))}
                </List>
                {exp.technologies && (
                  <Box mt={1}>
                    <Typography variant="subtitle2" fontWeight="bold">
                      Technologies
                    </Typography>
                    <Box display="flex" flexWrap="wrap">
                      {exp.technologies.map((tech, j) => (
                        <Chip key={j} label={tech} className={classes.chip} />
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            ))}
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default MemberDetail;